import { useState } from 'react';
import type { CreditQualityBucket, CreditQualityResponse } from '../api/chartDataTypes';
import { buildCreditQualitySummarySentence } from '../utils/creditQualityFormat';
import { formatReportDate, reportTypeLabel } from '../utils/perf10kFormat';
import { ChartDataSectionShell } from './ChartDataSectionShell';
import { CreditQualitySelector } from './CreditQualitySelector';

type CreditQualityChartPanelProps = {
    response: CreditQualityResponse;
};

const PROMPT_SUMMARY = 'Select a credit rating to hear its breakdown.';
const EMPTY_SUMMARY = 'No credit quality data available.';

/**
 * Credit Quality Breakdown orchestrator. Mirrors EconSectorChartPanel — a
 * point-in-time snapshot keyed by grade label rather than date. The shell's
 * banner opens with a prompt and the user tabs into the Credit Grade dropdown
 * to hear each bucket's weighting.
 */
export function CreditQualityChartPanel({ response }: CreditQualityChartPanelProps) {
    const { security, title, report_date, data } = response;
    const { buckets } = data;

    const [selectedBucket, setSelectedBucket] = useState<CreditQualityBucket | null>(null);

    const headerTitle = `${security.name} (${security.ticker}) — ${title}`;
    const headerSubtitle = `${reportTypeLabel(security.report_type)} · Report date: ${formatReportDate(report_date)}`;

    const hasBuckets = buckets.length > 0;

    let summary: string;
    let summaryHTML: string | undefined;
    if (!hasBuckets) {
        summary = EMPTY_SUMMARY;
    } else if (selectedBucket === null) {
        summary = PROMPT_SUMMARY;
    } else {
        const parts = buildCreditQualitySummarySentence(selectedBucket);
        summary = parts.text;
        summaryHTML = parts.html;
    }

    return (
        <ChartDataSectionShell
            title={headerTitle}
            subtitle={headerSubtitle}
            summary={summary}
            summaryHTML={summaryHTML}>
            {hasBuckets ? (
                <CreditQualitySelector
                    buckets={buckets}
                    idPrefix='credit_quality'
                    onSelectedBucketChange={setSelectedBucket}
                />
            ) : null}
        </ChartDataSectionShell>
    );
}
